/**
 * An email's *Cc* or *Bcc* — Aaron, Oct 26: "add bcc should also be an
 * option. in cc and bcc, you should be able to add multiple email addresses.
 * you should also be able to search for another user in the company adding
 * the thread in their inbox as well." `docs/activity.md` §7.1, §9.4.
 *
 * One box for the Inbox's composer, New email and a record's email. What is
 * typed becomes a chip at a comma, a semicolon, Enter or Tab (and when the box
 * is left); a paste is split into one chip per address (`splitAddresses`);
 * an entry that is not an address stays, flagged, until it is fixed or taken
 * off with its ×; Backspace in an empty box takes the last chip off.
 *
 * **The same box finds people in the company** (`GET /conversations/teammates`):
 * whoever the viewer sees on Activity's people button, and everybody else
 * locked. A person picked is a chip with their face — the email goes to their
 * own address, in this box, and the conversation goes into their Inbox.
 */
import { useEffect, useId, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import {
  addressKeyOf, firstNameOf, isEmailAddress, splitAddresses, type EmailTeammate,
} from '@rowted/shared'
import { Popover } from '../../components/overlays.js'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { Face } from '../faces.js'
import { Glyph } from '../glyphs.js'
import { FOCUS, PICKER_LAYER } from './kit.js'

/** One chip: an address somebody typed, or a person in the company. */
export type Copy =
  | { kind: 'address'; address: string }
  | { kind: 'person'; id: string; name: string; email: string; initials: string; colour: string | null }

export const copyKey = (copy: Copy): string =>
  (copy.kind === 'person' ? `person:${copy.id}` : `address:${addressKeyOf(copy.address)}`)

/** What a send names: the typed addresses and the people, apart — the server knows the people's own. */
export function copiesPayload(cc: readonly Copy[], bcc: readonly Copy[]): {
  cc: string[]; bcc: string[]; ccPeople: string[]; bccPeople: string[]
} {
  const addresses = (box: readonly Copy[]) => box.flatMap((one) => (one.kind === 'address' ? [one.address.trim()] : []))
  const people = (box: readonly Copy[]) => box.flatMap((one) => (one.kind === 'person' ? [one.id] : []))
  return { cc: addresses(cc), bcc: addresses(bcc), ccPeople: people(cc), bccPeople: people(bcc) }
}

/** *“dana@exmple” isn’t an email address.* — the first chip a send would be refused for. */
export function badCopyWords(...boxes: readonly (readonly Copy[])[]): string | null {
  for (const box of boxes) {
    const bad = box.find((one) => one.kind === 'address' && !isEmailAddress(one.address))
    if (bad && bad.kind === 'address') return `“${bad.address}” isn’t an email address.`
  }
  return null
}

/** A draft's copies as they were kept: chips, or — from before Oct 26 — the box's text. */
export function copiesFrom(stored: unknown): Copy[] {
  if (typeof stored === 'string') return splitAddresses(stored).map((address) => ({ kind: 'address', address }))
  if (!Array.isArray(stored)) return []
  return stored.filter((one): one is Copy => Boolean(one) && typeof one === 'object'
    && (((one as Copy).kind === 'address' && typeof (one as { address?: unknown }).address === 'string')
      || ((one as Copy).kind === 'person' && typeof (one as { id?: unknown }).id === 'string')))
}

/** The people a search finds, a beat after the typing stops. */
function useTeammates(q: string): EmailTeammate[] {
  const [found, setFound] = useState<{ q: string; items: EmailTeammate[] }>({ q: '', items: [] })
  const wanted = q.trim()
  useEffect(() => {
    if (!wanted || wanted.includes(',') || wanted.includes(';')) return undefined
    let live = true
    const timer = window.setTimeout(() => {
      activityRequest<{ items: EmailTeammate[] }>(`/conversations/teammates?q=${encodeURIComponent(wanted)}`)
        .then((answer) => { if (live) setFound({ q: wanted, items: answer.items }) })
        .catch(() => { if (live) setFound({ q: wanted, items: [] }) })
    }, 150)
    return () => { live = false; window.clearTimeout(timer) }
  }, [wanted])
  return wanted && found.q === wanted ? found.items : []
}

function Chip({ copy, phone, onRemove }: { copy: Copy; phone: boolean; onRemove: () => void }) {
  const bad = copy.kind === 'address' && !isEmailAddress(copy.address)
  const label = copy.kind === 'person' ? copy.name : copy.address
  return (
    <span data-copy={copy.kind} aria-invalid={bad || undefined}
      title={copy.kind === 'person' ? copy.email : bad ? 'Not an email address' : undefined}
      className={cn('inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full border font-semibold',
        phone ? 'h-8 pr-2 text-[14px]' : 'h-6 pr-1.5 text-[12px]',
        copy.kind === 'person' ? 'pl-[3px]' : phone ? 'pl-2.5' : 'pl-2',
        bad ? 'border-danger bg-danger-bg text-danger' : 'border-line bg-surface text-text')}>
      {copy.kind === 'person' && (
        <Face person={{ id: copy.id, name: copy.name, initials: copy.initials, colour: copy.colour }} size={phone ? 24 : 18} />
      )}
      <span className="truncate">{label}</span>
      <button type="button" aria-label={`Remove ${label}`} onClick={onRemove}
        className={cn('inline-flex shrink-0 items-center justify-center', bad ? 'text-danger' : 'text-text-muted hover:text-text',
          phone && 'h-8 w-6')}>
        <Glyph name="close" size={phone ? 13 : 11} stroke={2.4} />
      </button>
    </span>
  )
}

export function AddressBox({
  label, value, onChange, phone, look = 'field', autoFocus = false, taken = [], placeholder,
}: {
  label: 'Cc' | 'Bcc'
  value: readonly Copy[]
  onChange: (next: Copy[]) => void
  phone: boolean
  /** `field`: the New sheets' bordered box. `row`: inside the composer's own row, borderless. */
  look?: 'field' | 'row'
  autoFocus?: boolean
  /** Chips in the other box: a person there is not offered here again. */
  taken?: readonly Copy[]
  placeholder?: string
}) {
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  /* The row Enter picks; null is the box's own choice (below). */
  const [active, setActive] = useState<number | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const listId = useId()
  const found = useTeammates(open ? text : '')
  const chosen = new Set([...value, ...taken].map(copyKey))
  const shown = found.filter((one) => !chosen.has(`person:${one.id}`))
  const pickable = shown.filter((one) => !one.locked)
  const typed = text.trim()
  /* A whole address typed is the address — unless it is somebody's own, when it is them. */
  const auto = isEmailAddress(typed) && addressKeyOf(pickable[0]?.email ?? '') !== addressKeyOf(typed) ? -1 : 0
  const highlight = pickable.length === 0 ? -1 : active === null ? auto : Math.min(active, pickable.length - 1)
  const listOpen = open && typed.length > 0 && shown.length > 0

  useEffect(() => { setActive(null) }, [typed])

  const add = (more: Copy[]) => {
    const keys = new Set(value.map(copyKey))
    const next = [...value]
    for (const one of more) {
      if (keys.has(copyKey(one))) continue
      keys.add(copyKey(one))
      next.push(one)
    }
    onChange(next)
  }
  const commit = (raw: string) => {
    const parts = splitAddresses(raw)
    setText('')
    if (parts.length > 0) add(parts.map((address) => ({ kind: 'address' as const, address })))
  }
  const pick = (one: EmailTeammate) => {
    if (one.locked) return
    add([{ kind: 'person', id: one.id, name: one.name, email: one.email, initials: one.initials, colour: one.colour }])
    setText('')
    setOpen(false)
    input.current?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const key = event.key
    if ((key === 'Enter' || key === 'Tab') && typed) {
      event.preventDefault()
      if (listOpen && highlight >= 0) pick(pickable[highlight]!)
      else commit(text)
    } else if (key === 'Enter') {
      event.preventDefault()
    } else if ((key === ',' || key === ';') && typed) {
      event.preventDefault()
      commit(text)
    } else if (key === 'Backspace' && text === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    } else if ((key === 'ArrowDown' || key === 'ArrowUp') && listOpen && pickable.length > 0) {
      event.preventDefault()
      setActive(key === 'ArrowDown' ? Math.min(pickable.length - 1, highlight + 1) : Math.max(0, highlight - 1))
    } else if (key === 'Escape' && listOpen) {
      event.stopPropagation()
      setOpen(false)
    }
  }
  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text')
    if (!/[\s,;<]/.test(pasted.trim())) return
    event.preventDefault()
    commit(`${text} ${pasted}`)
  }

  const row = phone ? 'min-h-[52px] px-4 text-[15px]' : 'px-3 py-[7px] text-[13px]'
  return (
    <>
      <div ref={box} role="group" aria-label={label} onClick={() => input.current?.focus()}
        className={cn('flex min-w-0 flex-1 flex-wrap items-center gap-1',
          look === 'field' && cn('w-full cursor-text border border-line bg-surface text-text', FOCUS,
            phone ? 'min-h-[46px] rounded-[10px] px-2 py-[6px] text-[15px]' : 'min-h-9 rounded-lg px-[7px] py-[5px] text-[13px]'))}>
        {value.map((copy) => (
          <Chip key={copyKey(copy)} copy={copy} phone={phone}
            onRemove={() => onChange(value.filter((one) => copyKey(one) !== copyKey(copy)))} />
        ))}
        <input ref={input} value={text} autoFocus={autoFocus} aria-label={label} role="combobox"
          aria-expanded={listOpen} aria-controls={listId} aria-autocomplete="list"
          placeholder={value.length === 0 ? placeholder ?? 'Name or email address' : undefined}
          onChange={(event) => { setText(event.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onBlur={() => { if (typed) commit(text) }}
          onKeyDown={onKeyDown} onPaste={onPaste}
          className={cn('min-w-[90px] flex-1 bg-transparent outline-none focus-visible:shadow-none placeholder:text-text-faint',
            phone ? 'h-8' : 'h-6')} />
      </div>
      <Popover open={listOpen} onClose={() => setOpen(false)} anchor={box.current} label={`People for ${label}`}
        width={Math.max(280, box.current?.offsetWidth ?? 0)} layer={PICKER_LAYER} maxHeight={320} className="py-1">
        <div id={listId} role="listbox" aria-label="People in the company" className="flex min-h-0 flex-col overflow-y-auto">
          {shown.map((one) => {
            const at = pickable.indexOf(one)
            return one.locked ? (
              <div key={one.id} role="option" aria-selected={false} aria-disabled
                /* Pressing it leaves what was typed in the box rather than making it a chip. */
                onMouseDown={(event) => event.preventDefault()}
                className={cn('flex w-full items-center gap-2.5 opacity-60', row)}>
                <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-line-soft text-text-muted">
                  <Glyph name="lock" size={12} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-semibold">{one.name}</span>
                  <span className={cn('truncate text-text-muted', phone ? 'text-[12.5px]' : 'text-[11.5px]')}>
                    {one.place ? `${one.place} — ` : ''}you can’t add {firstNameOf(one.name)}
                  </span>
                </span>
              </div>
            ) : (
              <button key={one.id} type="button" role="option" aria-selected={at === highlight}
                onMouseDown={(event) => event.preventDefault()} onClick={() => pick(one)}
                className={cn('flex w-full items-center gap-2.5 text-left hover:bg-hover', row, at === highlight && 'bg-hover')}>
                <Face person={one} size={22} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-semibold">{one.name}</span>
                  <span className={cn('truncate text-text-muted', phone ? 'text-[12.5px]' : 'text-[11.5px]')}>{one.email}</span>
                </span>
                <span className={cn('shrink-0 text-text-faint', phone ? 'text-[12px]' : 'text-[11px]')}>in their Inbox too</span>
              </button>
            )
          })}
        </div>
      </Popover>
    </>
  )
}
